'use client';

import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useState } from 'react';

import { BragConfirmModal } from '@/modules/players/v3/components/shared/brag/brag-confirm-modal';
import { useBragAchievement } from '@/modules/players/v3/hooks/use-brag-achievement';
import { fireConfetti } from '@/modules/players/v3/utils/confetti';

type BragButtonVariant = 'outline' | 'solid';

interface BragButtonProps {
  achievementId?: number;
  achievementName?: string;
  variant?: BragButtonVariant;
  className?: string;
  label?: string;
  showIcon?: boolean;
}

const VARIANT_CLASSNAME: Record<BragButtonVariant, string> = {
  outline: '!border !border-brand-700 !bg-white !text-brand-700',
  solid: '!bg-brand !text-white',
};

const BRAG_LABEL = formatText({ text: 'Brag', role: TextRole.ButtonLabel });

const SPARK_CLASSNAMES = [
  'bg-warning-300 [--brag-x:-34px] [--brag-y:-30px] [animation-delay:0s]',
  'bg-error-400 [--brag-x:-18px] [--brag-y:-38px] [animation-delay:0.12s]',
  'bg-lightblue-400 [--brag-x:-4px] [--brag-y:-32px] [animation-delay:0.05s]',
  'bg-warning-300 [--brag-x:12px] [--brag-y:-40px] [animation-delay:0.2s]',
  'bg-error-400 [--brag-x:26px] [--brag-y:-30px] [animation-delay:0.09s]',
  'bg-lightblue-400 [--brag-x:38px] [--brag-y:-36px] [animation-delay:0.16s]',
];

export function BragButton({
  achievementId,
  achievementName,
  variant = 'outline',
  className,
  label = BRAG_LABEL,
  showIcon = false,
}: BragButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { brag, isPending } = useBragAchievement();

  const handleConfirm = () => {
    if (achievementId === undefined) return;
    brag(achievementId, () => {
      setConfirmOpen(false);
      fireConfetti();
    });
  };

  return (
    <span className={clsx('group/brag relative inline-flex', className)}>
      <span
        aria-hidden
        className='pointer-events-none absolute inset-x-0 top-0 flex justify-center'
      >
        {SPARK_CLASSNAMES.map((sparkClassName) => (
          <span
            key={sparkClassName}
            className={clsx(
              'absolute top-0 size-1.5 rounded-[1px] opacity-0',
              'motion-safe:group-hover/brag:animate-brag-pop',
              'motion-safe:group-has-[:focus-visible]/brag:animate-brag-pop',
              sparkClassName
            )}
          />
        ))}
      </span>
      <Button
        className={clsx('w-full flex-1 justify-center gap-2', VARIANT_CLASSNAME[variant])}
        onClick={() => setConfirmOpen(true)}
        disabled={achievementId === undefined}
      >
        {showIcon ? <FontAwesomeIcon icon={faBullhorn} className='size-4' /> : null}
        {label}
      </Button>
      <BragConfirmModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirm}
        isPending={isPending}
        achievementName={achievementName}
      />
    </span>
  );
}
